import { Outlet, Link, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { 
  MessageSquare, 
  Menu, 
  X, 
  Phone, 
  Mail,
  User,
  Settings,
  LogOut,
  LayoutDashboard
} from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/lib/utils'
import LoginModal from '@/components/LoginModal'
import { useCurrentUser, useLogout } from '@/hooks/useAuth'

const navigation = [
  { name: '首页', href: '/' },
  { name: '产品功能', href: '/features' },
  { name: '价格方案', href: '/pricing' },
  { name: '开发文档', href: '/docs' },
  { name: '客户案例', href: '/cases' },
  { name: '帮助中心', href: '/help' },
]

export default function MarketingLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [showLoginModal, setShowLoginModal] = useState(false)
  const location = useLocation()
  const { data: user, isLoading } = useCurrentUser()
  const logout = useLogout()

  return (
    <div className="min-h-screen bg-white">
      {/* 导航栏 */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-2">
                <div className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <MessageSquare className="h-5 w-5 text-white" />
                </div>
                <span className="text-xl font-bold text-gray-900">SMS平台</span>
              </Link>
            </div>

            {/* 桌面端导航 */}
            <div className="hidden md:flex items-center space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "text-sm font-medium transition-colors hover:text-blue-600",
                    location.pathname === item.href
                      ? "text-blue-600"
                      : "text-gray-700"
                  )}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* 右侧按钮 */}
            <div className="hidden md:flex items-center space-x-4">
              {!isLoading && user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-white" />
                      </div>
                      <span className="hidden lg:block">{user.name}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="flex items-center justify-start gap-2 p-2">
                      <div className="flex flex-col space-y-1 leading-none">
                        <p className="font-medium">{user.name}</p>
                        <p className="w-[200px] truncate text-sm text-muted-foreground">
                          {user.email}
                        </p>
                      </div>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center">
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        进入工作台
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard/account" className="flex items-center">
                        <Settings className="mr-2 h-4 w-4" />
                        账户设置
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      className="text-red-600 focus:text-red-600"
                      onClick={() => logout()}
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      退出登录
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowLoginModal(true)}
                  >
                    登录
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => setShowLoginModal(true)}
                  >
                    免费试用
                  </Button>
                </>
              )}
            </div>

            {/* 移动端菜单按钮 */}
            <div className="md:hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </Button>
            </div>
          </div>

          {/* 移动端菜单 */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200 py-4">
              <div className="space-y-2">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
                <div className="border-t border-gray-200 pt-4 mt-4">
                  {!isLoading && user ? (
                    <>
                      <div className="px-3 py-2 mb-2">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{user.name}</p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                        </div>
                      </div>
                      <Link
                        to="/dashboard"
                        className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <LayoutDashboard className="inline mr-2 h-4 w-4" />
                        进入工作台
                      </Link>
                      <button
                        className="block w-full text-left px-3 py-2 text-base font-medium text-red-600 hover:bg-red-50 rounded-md mt-2"
                        onClick={() => {
                          setMobileMenuOpen(false)
                          logout()
                        }}
                      >
                        <LogOut className="inline mr-2 h-4 w-4" />
                        退出登录
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="block w-full text-left px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md"
                        onClick={() => {
                          setMobileMenuOpen(false)
                          setShowLoginModal(true)
                        }}
                      >
                        登录
                      </button>
                      <button
                        className="block w-full text-left px-3 py-2 text-base font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md mt-2"
                        onClick={() => {
                          setMobileMenuOpen(false)
                          setShowLoginModal(true)
                        }}
                      >
                        免费试用
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* 主内容 */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* 页脚 */}
      <footer className="bg-gray-900 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* 公司信息 */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <div className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <MessageSquare className="h-5 w-5 text-white" />
                </div>
                <span className="text-xl font-bold">短信服务平台</span>
              </div>
              <p className="text-gray-400 mb-4 max-w-md">
                专业的企业级短信服务平台，为您提供稳定、高效、安全的短信解决方案。
              </p>
            </div>

            {/* 产品服务 */}
            <div>
              <h3 className="text-lg font-semibold mb-4">产品服务</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/features" className="hover:text-white">短信发送</Link></li>
                <li><Link to="/features" className="hover:text-white">语音通知</Link></li>
                <li><Link to="/features" className="hover:text-white">国际短信</Link></li>
                <li><Link to="/features" className="hover:text-white">API接口</Link></li>
              </ul>
            </div>

            {/* 帮助支持 */}
            <div>
              <h3 className="text-lg font-semibold mb-4">帮助支持</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/docs" className="hover:text-white">开发文档</Link></li>
                <li><Link to="/help" className="hover:text-white">常见问题</Link></li>
                <li><Link to="/help" className="hover:text-white">技术支持</Link></li>
                <li><Link to="/help" className="hover:text-white">服务条款</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <div className="flex items-center justify-center space-x-4">
              <a 
                href="https://beian.miit.gov.cn/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-white transition-colors"
              >
                京ICP备2025155800号-1
              </a>
              <span className="text-gray-600">|</span>
              <a 
                href="http://www.beian.gov.cn/portal/registerSystemInfo?recordcode=11010102011412" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center space-x-1 hover:text-white transition-colors"
              >
                <img 
                  src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsTAAALEwEAmpwYAAABhElEQVQ4y6WUzUrDQBSFv0mbtP5QRBQXLpQX8AJ6g3gDHsE7aO0VLqCnpYU/gI1gQZsWihQPogdbnFgQb6JN07Q5m6R4SDb54XNmdubNzDiT5IXFJU9Obr5LEAgwDFQBS/qCoqK8vPz+Z2CSLxAK0Ag2gWWOYRdYA5vAh4LGaqRShSyhGDqBIJfjCJVYGWwZPwQawCewCu8BM0Fj4p0LT9BdMJvXwBRYOj8YqYKb6AKnwXrQCMbJhgKq/kNzwSawCWwC88CzsARsAhvAvrBdWEoKYBg9gRngGlgGNiLLx9gCHgX2gX3gC1gDHoA1YBuYAHaBFWA3aA0tgHVgB9gFdoC2sAqsA5vAerAO7APrwB6wDewCu8A+sA+sAxu1hZrCFtgGG8A2sAVsA+vAFrANbAHbwHawDewCe8A+sAc8gEsiWBWswCawAewCu8AesAdsA7vANrAP7APbwDa0hfmwBawD28A2sA1sA9vANrANbAfzYAuYA3aAbWAP2AM2gE2sCXXgEjAL7AH7YBG4BbaARaARWAaKccz/DzxVcFfAB2gTI/lk/BnAAAAAElFTkSuQmCC"
                  alt="公安备案"
                  className="h-4 w-4"
                />
                <span>京公网安备</span>
              </a>
            </div>
            <p className="mt-2">&copy; 2024 短信服务平台. 保留所有权利.</p>
          </div>
        </div>
      </footer>

      {/* 登录 Modal */}
      <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} />
    </div>
  )
}
